import type { AgentIconType } from '@confluence/agent-user-dual-avatar/entry-points/agentIconType.ts';

export enum AccountType {
	NORMAL = 'normal',
	GUEST = 'guest',
	ANONYMOUS = 'anonymous',
}

/**
 * A person a page view was attributed to (an "on behalf of" user), used to build
 * the byline attribution line ("with {name} • {time}"). Derived from a page
 * view's `relatedUser`.
 */
export type AttributedUser = {
	id: string;
	name: string;
	lastViewedAt: string;
};

export type KnownAccount = {
	id: string;
	name: string;
	avatarURL: string;
	accountType: AccountType.GUEST | AccountType.NORMAL;
	isDeactivated?: boolean;
	lastViewedAt?: string;
	localIds?: string[];
	isEngaged: boolean | null;
	/**
	 * True when the underlying page view was made by an AI agent rather than a
	 * human. Canonically derived from the analytics `accountType === 'AGENT'`
	 * (with a non-empty `agentType` as a secondary signal) — mirrors
	 * `agentViewers.ts` / the page-analytics ViewersTable. Optional and additive:
	 * existing consumers that don't distinguish agents can ignore it. Only
	 * reliably set for accounts returned by `contentAnalyticsViewsByUser` (i.e.
	 * viewers) — non-viewers are classified separately (see `useMentionedAgents`).
	 */
	isAgent?: boolean;
	/**
	 * Fixed fallback icon for an unresolved agent (see {@link AgentIconType}).
	 * Undefined for resolved agents (use the hexagon `AgentAvatar`) and for
	 * humans.
	 */
	agentIcon?: AgentIconType;
	/**
	 * People this agent's views were made on behalf of, aggregated across the
	 * agent's grouped page views. Only set for agent accounts when there is at
	 * least one attributed user; drives the byline attribution secondary text.
	 */
	attributedUsers?: AttributedUser[];
	/**
	 * The viewer's `userProfile.id` when grouping rewrites `id` to
	 * `${groupKey}:${relatedUserId}`. Mentions matching uses this so an ADF
	 * mention AAID can still match a grouped agent row.
	 */
	identityAccountId?: string;
};

export type AnonymousAccount = {
	id: null;
	name: null;
	avatarURL: null;
	accountType: AccountType.ANONYMOUS;
	isDeactivated?: false;
	lastViewedAt?: string;
	localIds?: string[];
	isEngaged: boolean | null;
};

export type Account = KnownAccount | AnonymousAccount;
